import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { recentEntries, RECENT_LIMIT } from '@/lib/utils';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowUUpLeft, MagnifyingGlass, Trash } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import AdvanceRefundDialog from '@/components/AdvanceRefundDialog';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso; }
};

/**
 * RefundPage
 * ----------
 * Top-level page for issuing customer advance refunds. Pairs a searchable
 * list of customers holding an advance with the existing AdvanceRefundDialog.
 * Also lists every refund recorded so far for quick auditing/voiding.
 */
export default function RefundPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [history, setHistory] = useState([]);
  const [accountsById, setAccountsById] = useState({});
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(null);

  const refresh = () => {
    api.get('/customers').then(r => setCustomers((r.data || []).filter(c => (c.advance_balance || 0) > 0))).catch(() => {});
    api.get('/finance?type=advance_refund').then(r => setHistory(recentEntries(r.data || [], ''))).catch(() => {});
    api.get('/finance/accounts').then(r => {
      const map = {};
      (r.data || []).forEach(a => { map[a.id] = a.name; });
      setAccountsById(map);
    }).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  // Deep-link support: ?customer=<id> opens the dialog pre-selected.
  useEffect(() => {
    const cid = search.get('customer');
    if (cid && customers.length > 0) {
      const c = customers.find(x => x.id === cid);
      if (c) setActive(c);
    }
  }, [search, customers]);

  const filtered = customers.filter(c =>
    !query.trim() || c.name.toLowerCase().includes(query.toLowerCase()) || (c.phone || '').includes(query)
  );

  const customerById = (id) => customers.find(c => c.id === id);

  const deleteRefund = async (txn) => {
    if (!window.confirm(`Reverse refund ${txn.voucher_number}? This will restore the customer's advance and the source account balance.`)) return;
    try {
      await api.delete(`/finance/${txn.id}`);
      toast.success('Refund reversed');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse refund');
    }
  };

  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/finance')}
        className="text-xs text-[#52525B] hover:text-[#0A0A0A]"
        data-testid="refund-back-btn"
      >
        ← Back to Finance
      </Button>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT — Customers holding an advance */}
        <Card className="rounded-xl border-[#E4E4E7]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[#0A0A0A]">Customers Holding Advance</h2>
              <span className="text-xs text-[#A1A1AA]">{customers.length}</span>
            </div>
            <div className="relative mb-3">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
              <Input
                placeholder="Search by name or phone..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="rounded-lg pl-9 h-9 text-sm"
                data-testid="refund-search-input"
              />
            </div>
            {filtered.length === 0 ? (
              <p className="text-sm text-[#A1A1AA] italic py-6 text-center">No customers with advance balance.</p>
            ) : (
              <div className="max-h-[480px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#E4E4E7]">
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Customer</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Advance</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(c => (
                      <TableRow key={c.id} className="border-[#F4F4F5]" data-testid={`refund-cust-row-${c.id}`}>
                        <TableCell>
                          <p className="text-xs font-medium">{c.name}</p>
                          <p className="text-[10px] text-[#A1A1AA] font-mono">{c.phone || '—'}</p>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm font-mono font-bold text-[#3B82F6]">{fmt(c.advance_balance)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setActive(c)}
                            className="h-7 px-2 text-xs border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2]"
                            data-testid={`refund-open-btn-${c.id}`}
                          >
                            <ArrowUUpLeft size={12} className="mr-1" /> Refund
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* RIGHT — Recent refund history */}
        <Card className="rounded-xl border-[#E4E4E7]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[#0A0A0A]">Refund History</h2>
              <span className="text-xs text-[#A1A1AA]" data-testid="refund-history-caption">Last {RECENT_LIMIT} refunds</span>
            </div>
            {history.length === 0 ? (
              <p className="text-sm text-[#A1A1AA] italic py-6 text-center">No refunds recorded yet.</p>
            ) : (
              <div className="max-h-[480px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#E4E4E7]">
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Voucher</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Customer</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">From</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Amount</TableHead>
                      <TableHead className="w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map(h => (
                      <TableRow key={h.id} className="border-[#F4F4F5]" data-testid={`refund-history-row-${h.voucher_number}`}>
                        <TableCell className="text-xs font-mono font-semibold text-[#3B82F6]">{h.voucher_number}</TableCell>
                        <TableCell className="text-xs">{fmtDate(h.created_at)}</TableCell>
                        <TableCell className="text-xs">{customerById(h.customer_id)?.name || h.description || '—'}</TableCell>
                        <TableCell className="text-xs">{accountsById[h.account] || '—'}</TableCell>
                        <TableCell className="text-right text-xs font-mono font-semibold text-[#EF4444]">{fmt(h.amount)}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteRefund(h)}
                            className="h-6 w-6 p-0 text-[#EF4444] hover:bg-[#FEF2F2]"
                            data-testid={`refund-delete-${h.voucher_number}`}
                            title="Reverse this refund"
                          >
                            <Trash size={12} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AdvanceRefundDialog
        key={active?.id || 'closed'}
        open={!!active}
        onOpenChange={(v) => { if (!v) setActive(null); }}
        customer={active}
        onSaved={refresh}
      />
    </div>
  );
}
